import React, { useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { Input } from 'formik-antd';
import { FormikProps } from 'formik';

import FormItem from 'components/formik/FormItem';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import {
  countriesSelector,
  isCountriesLoadingSelector,
  countryCitiesSelector,
  isCountryCitiesLoadingSelector,
} from 'redux/selectors/regionSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import { WorkerModalFormValues } from '../WorkerModal';

type Props = {
  formik: FormikProps<WorkerModalFormValues>;
};

export default function InternationalWorkerStateForm(props: Props) {
  const { formik } = props;
  const [selectedCountry, setSelectedCountry] = useState(
    formik.initialValues.country,
  );
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const cities = useAppSelector(countryCitiesSelector);
  const isCountryCitiesLoading = useAppSelector(isCountryCitiesLoadingSelector);

  const filteredCities = useMemo(
    () => cities.filter((city) => city.countryId === selectedCountry),
    [selectedCountry, cities],
  );

  const handleCountryChange = (
    countryId: number,
    formik: FormikProps<WorkerModalFormValues>,
  ) => {
    setSelectedCountry(countryId);

    formik.setFieldValue('city', '', false);
    formik.setFieldValue('state', '', false);
    formik.setFieldValue('internationalCityName', '', false);
    formik.setFieldValue('internationalStateName', '', false);
  };

  return (
    <Row gutter={20}>
      <Col xs={24} md={6}>
        <FormItem name="country" label="Country *">
          <FormikSelectWithLoading
            isLoading={isCountriesLoading}
            name="country"
            showSearch
            placeholder="Select country"
            optionFilterProp="children"
            onChange={(value: number) => handleCountryChange(value, formik)}
          >
            {countries.map((country) => (
              <FormikSelectWithLoading.Option
                key={`select-country-${country.id}`}
                value={country.id || -1}
              >
                {country.name}
              </FormikSelectWithLoading.Option>
            ))}
          </FormikSelectWithLoading>
        </FormItem>
      </Col>

      <Col xs={24} md={6}>
        <FormItem
          data-id="workerStateField"
          name="internationalStateName"
          label="State *"
        >
          <Input
            name="internationalStateName"
            placeholder="Enter state"
            disabled={!formik.values.country}
            autoComplete="off"
          />
        </FormItem>
      </Col>

      <Col xs={24} md={6}>
        <FormItem
          data-id="workerCityField"
          name="internationalCityName"
          label="City *"
        >
          <FormikAutoCompleteWithLoading
            isLoading={isCountryCitiesLoading}
            name="internationalCityName"
            filterOption
            disabled={!formik.values.internationalStateName}
            placeholder="Enter city"
            showSearch
          >
            {filteredCities.map((city) => (
              <FormikAutoCompleteWithLoading.Option
                key={`city-state-${city.id}`}
                value={city.name || ''}
              >
                {city.name}
              </FormikAutoCompleteWithLoading.Option>
            ))}
          </FormikAutoCompleteWithLoading>
        </FormItem>
      </Col>

      <Col xs={24} md={6}>
        <FormItem data-id="workerAddressField" label="Address *" name="address">
          <Input name="address" placeholder="Enter address" autoComplete="off" />
        </FormItem>
      </Col>

      <Col xs={24} md={6}>
        <FormItem data-id="workerZipCodeField" label="ZIP" name="zip">
          <Input name="zip" placeholder="Enter ZIP code" autoComplete="off" />
        </FormItem>
      </Col>
    </Row>
  );
}
